import { IssuedGiftCardsTable } from "./IssuedGiftCardsTable.tsx";
import { LoadingRows } from "../UI/Loading/LoadingRows.tsx";
import { useState } from "react";
import { useIssuedGiftCards } from "./useIssuedGiftCards.ts";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Layout } from "../UI/Layout/Layout.tsx";
import { ScreenTitleBlock } from "../UI/Layout/ScreenTitleBlock.tsx";
import { Card } from "../UI/Card.tsx";
import { TextButton } from "../UI/TextButton.tsx";
import { ExportCsvModal } from "./ExportCsvModal.tsx";

dayjs.extend(utc);
dayjs.extend(timezone);

export const IssuedGiftCardsScreenPath = "/issued-gift-cards";

export function IssuedGiftCardsScreen() {
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [receiverEmail, setReceiverEmail] = useState<string>("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const convertToUTCStartOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs
      .tz(dateString, "Europe/Rome")
      .startOf("day")
      .utc()
      .toISOString();
  };

  const convertToUTCEndOfDay = (dateString: string) => {
    if (!dateString) return undefined;
    return dayjs.tz(dateString, "Europe/Rome").endOf("day").utc().toISOString();
  };

  const { data, isLoading } = useIssuedGiftCards({
    limit,
    offset,
    fromDate: fromDate ? convertToUTCStartOfDay(fromDate) : undefined,
    toDate: toDate ? convertToUTCEndOfDay(toDate) : undefined,
    receiverEmail: receiverEmail || undefined,
  });

  const handleNextPage = () => {
    if (data.hasNextPage) {
      setOffset(offset + limit);
    }
  };

  const handlePreviousPage = () => {
    if (data.hasPreviousPage) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  return (
    <Layout>
      <ExportCsvModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
      <Card>
        <ScreenTitleBlock
          title={"Issued Gift Cards"}
          ctaLabel={"Export CSV"}
          ctaOnClick={() => setIsExportModalOpen(true)}
        />
      </Card>
      <div className={"mt-4"}>
        <Card>
          <div className={"mb-4 grid grid-cols-1 md:grid-cols-3 gap-4"}>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
            <div>
              <label className={"block text-sm font-medium mb-2"}>
                Receiver Email
              </label>
              <input
                type="email"
                value={receiverEmail}
                onChange={(e) => setReceiverEmail(e.target.value)}
                placeholder="Filter by receiver email"
                className={"w-full px-3 py-2 border border-gray-300 rounded-md"}
              />
            </div>
          </div>

          {isLoading ? (
            <LoadingRows />
          ) : (
            <div>
              <IssuedGiftCardsTable issuedGiftCards={data.issuedGiftCards} />

              <div className={"flex w-full justify-between mt-4"}>
                <div>
                  {data.hasPreviousPage ? (
                    <TextButton
                      onClick={handlePreviousPage}
                      label={"Previous page"}
                    />
                  ) : null}
                </div>
                <div>
                  {data.hasNextPage ? (
                    <TextButton onClick={handleNextPage} label={"Next page"} />
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
}
